import { ActionType, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import {
    IPrEmploymentEntity,
    IPrInitialDataEntity,
    PrEmploymentInitialLongTermTimeBonusEntity
} from "@odata/GeneratedEntityTypes";
import { isEqual } from "lodash";

import { withPermissionContext } from "../../../../contexts/permissionContext/withPermissionContext";
import DialogFormView, { IDialogFormViewProps } from "../../../../views/formView/DialogFormView";
import { IContextInitArgs, IFormStorageSaveResult } from "../../../../views/formView/FormStorage";
import { LongTermTimeBonusesPath } from "./EmploymentInitialData.def";
import {
    EmployeeInitialDataPath,
    fetchEmployeeInitialData,
    fetchInitialDataLimits,
    getEmployeeInitialData,
    hasInitialDataTabError,
    IEmploymentInitialDataCustomData,
    IInitialDataLimits,
    InitialDataTab,
    setActiveInitialDataTab
} from "./EmploymentInitialData.utils";

type TProps = IDialogFormViewProps<IPrEmploymentEntity, IEmploymentInitialDataCustomData>;

class EmploymentInitialDataFormView extends DialogFormView<IPrEmploymentEntity, TProps> {
    originalEmployeeInitialData: IPrInitialDataEntity;

    getAdditionalLoadPromise(args: IContextInitArgs): (Promise<unknown | void>)[] {
        const employment = args.data as IPrEmploymentEntity;
        return [
            fetchEmployeeInitialData(this.props.storage, employment?.Employee?.Id),
            fetchInitialDataLimits(this.props.storage, employment?.DateCalculatePayslipStart)
        ];
    }

    onAfterLoad(): Promise<void> {
        const { storage } = this.props;
        this.originalEmployeeInitialData = this.getAdditionalResults()[0] as IPrInitialDataEntity;
        storage.setCustomData({ commonFieldLimits: this.getAdditionalResults()[1] as IInitialDataLimits });
        storage.setValueByPath(EmployeeInitialDataPath, getEmployeeInitialData(storage.data.entity, this.originalEmployeeInitialData));

        return super.onAfterLoad();
    }

    async save(): Promise<IFormStorageSaveResult> {
        const { storage } = this.props;
        const validationResult = await storage.validateAll();
        storage.refreshFields();

        if (validationResult.hasError) {
            const hasEmploymentError = hasInitialDataTabError(storage, InitialDataTab.Employment);
            setActiveInitialDataTab(storage, hasEmploymentError ? InitialDataTab.Employment : InitialDataTab.Common);
            return null;
        }

        const data = { ...storage.data.entity };
        if (isEqual(data.Employee?.InitialData, this.originalEmployeeInitialData)) {
            data.Employee = { ...data.Employee };
            delete data.Employee.InitialData;
        }

        return {
            bindingContext: storage.data.bindingContext,
            data
        };
    }

    handleLineItemsAction(args: ISmartFastEntriesActionEvent): void {
        const { storage } = this.props;
        const bonusesBindingContext = storage.data.bindingContext.navigate(LongTermTimeBonusesPath);

        if (args.actionType === ActionType.Add && args.bindingContext.isSame(bonusesBindingContext)) {
            const bonus = args.affectedItems?.[0];
            const dateMonthBindingContext = args.bindingContext.addKey(bonus).navigate(PrEmploymentInitialLongTermTimeBonusEntity.DateMonth);
            const minDate = storage.getInfo(dateMonthBindingContext)?.fieldSettings?.minDate;

            if (minDate) {
                bonus.DateMonth = minDate;
            }
        }

        super.handleLineItemsAction(args);
    }

}

export default withPermissionContext(EmploymentInitialDataFormView);
